// frontend/src/pages/MessagingPage.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-12-16
// Updated:      2026-09-25 10:45 BST
//
// Desktop resize regression fix (2026-09-25):
//   - Removed the viewport-derived ReactGridLayout `maxRows` ceiling.
//   - Desktop widgets may now grow vertically beyond the initial viewport and the page scrolls naturally.
//   - Layout persistence preserves x/y/w/h while preventing negative y positions.
//   - Page-owned widget positioning is no longer forced back inside viewport rows.
//   - Existing mobile single-column presentation and mobile sizing behaviour are unchanged.
//
// Description:  Messaging Centre page using the standard grid layout.
//               Hosts messaging-focused widgets such as:
//                 • Messaging Template Library
//                 • Open Conversations
//                 • Attention Required
//                 • Messaging / Channel Health
//
// Mobile Responsiveness (2026-09-04):
//   - Added a dedicated mobile presentation at <= 768px.
//   - Mobile renders configured Messaging widgets in a natural single-column flow.
//   - Mobile ordering follows the persisted desktop layout position (y, then x).
//   - react-grid-layout drag/resize behaviour remains desktop-only.
//   - Mobile rendering never persists layout changes, so viewing Messaging on a phone
//     cannot alter the user's desktop widget arrangement.
//   - Reflowed the page heading and Add Widget action for compact mobile widths.
//   - Reduced mobile page gutters while preserving page-owned portal modal behaviour,
//     template refresh handling, localStorage keys, and desktop layout behaviour.
//
// Notes:
//   - Page owns modals (portalled) to prevent clipping inside widgets.

import React, { useState, useEffect, useCallback, useRef } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import WidgetWrapper from "../components/WidgetWrapper";
import AddWidgetModal from "../components/AddWidgetModal";
import { markWidgetUsedIn } from "../widgets/WidgetRegistry";
import { useTheme } from "../context/ThemeContext.jsx";

import MessagingTemplateLibraryWidget from "../widgets/MessagingTemplateLibraryWidget.jsx";
import OpenConversationsWidget from "../widgets/OpenConversationsWidget.jsx";
import MessagesAttentionWidget from "../widgets/MessagesAttentionWidget.jsx";
import MessagingHealthWidget from "../widgets/MessagingHealthWidget.jsx";

// ✅ Page-level modal (no clipping)
import WhatsAppTemplateCreatorModal from "../components/WhatsAppTemplateCreatorModal.jsx";

const STORAGE_KEY_LAYOUT = "messagingLayout";
const STORAGE_KEY_WIDGETS = "messagingWidgets";

const MOBILE_BREAKPOINT = 768;

function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const apply = () => setIsMobile(mediaQuery.matches);

    apply();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", apply);
    } else {
      mediaQuery.addListener(apply);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", apply);
      } else {
        mediaQuery.removeListener(apply);
      }
    };
  }, [breakpoint]);

  return isMobile;
}

const defaultLayout = [
  { i: "messaging_template_library", x: 0, y: 0, w: 6, h: 6, minW: 4, minH: 4, maxW: 12 },
  { i: "open_conversations", x: 6, y: 0, w: 6, h: 6, minW: 4, minH: 4, maxW: 12 },
  { i: "messages_attention", x: 0, y: 6, w: 8, h: 5, minW: 4, minH: 4, maxW: 12 },
  { i: "messaging_health", x: 8, y: 6, w: 4, h: 5, minW: 3, minH: 4, maxW: 12 },
];

const normaliseLayout = (layout) =>
  (Array.isArray(layout) ? layout : []).map((item) => {
    const currentY = Number.isFinite(item?.y) ? item.y : 0;
    return { ...item, y: Math.max(0, currentY) };
  });

const MessagingPage = () => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();

  const [widgets, setWidgets] = useState({});
  const [layout, setLayout] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [gridWidth, setGridWidth] = useState(1200);

  // ✅ Template creation modal state (page-owned)
  const [showWaCreator, setShowWaCreator] = useState(false);
  const [waCreatorTenantId, setWaCreatorTenantId] = useState(1);
  const [templateRefreshKey, setTemplateRefreshKey] = useState(0);

  const gridRef = useRef(null);

  const containerPadding = 24;
  const headerHeight = 64;
  const rowHeight = 40;

  useEffect(() => {
    const node = gridRef.current;
    if (!node) return undefined;

    const updateWidth = () => {
      setGridWidth(node.offsetWidth || 1200);
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  // ✅ Listen for widget events to open creator modal (prevents clipping)
  useEffect(() => {
    const onOpenCreator = (evt) => {
      const tenantId = evt?.detail?.tenantId ?? 1;
      setWaCreatorTenantId(tenantId);
      setShowWaCreator(true);
    };

    window.addEventListener("anpr:whatsappTemplate:create", onOpenCreator);
    return () => window.removeEventListener("anpr:whatsappTemplate:create", onOpenCreator);
  }, []);

  const resolveWidgetComponent = (id) => {
    switch (id) {
      case "messaging_template_library":
        return <MessagingTemplateLibraryWidget tenantId={1} refreshKey={templateRefreshKey} />;
      case "open_conversations":
        return <OpenConversationsWidget />;
      case "messages_attention":
        return <MessagesAttentionWidget />;
      case "messaging_health":
        return <MessagingHealthWidget />;
      default:
        return (
          <div style={{ padding: "1rem", color: "red" }}>
            Unknown messaging widget: {id}
          </div>
        );
    }
  };

  useEffect(() => {
    try {
      const storedLayout = JSON.parse(localStorage.getItem(STORAGE_KEY_LAYOUT));
      const storedWidgetIds = JSON.parse(localStorage.getItem(STORAGE_KEY_WIDGETS));

      if (Array.isArray(storedWidgetIds) && storedWidgetIds.some((w) => typeof w !== "string")) {
        console.warn("Corrupted widget storage detected for MessagingPage. Clearing...");
        localStorage.removeItem(STORAGE_KEY_LAYOUT);
        localStorage.removeItem(STORAGE_KEY_WIDGETS);
        throw new Error("Corrupted widget storage");
      }

      if (Array.isArray(storedLayout) && Array.isArray(storedWidgetIds)) {
        const normalised = normaliseLayout(storedLayout);
        setLayout(normalised);

        const components = {};
        storedWidgetIds.forEach((id) => {
          components[id] = resolveWidgetComponent(id);
          markWidgetUsedIn(id, "messaging");
        });

        setWidgets(components);
        return;
      }
    } catch {
      console.warn("Using default layout for MessagingPage");
    }

    setLayout(defaultLayout);
    const initialWidgets = {
      messaging_template_library: resolveWidgetComponent("messaging_template_library"),
      open_conversations: resolveWidgetComponent("open_conversations"),
      messages_attention: resolveWidgetComponent("messages_attention"),
      messaging_health: resolveWidgetComponent("messaging_health"),
    };
    setWidgets(initialWidgets);

    markWidgetUsedIn("messaging_template_library", "messaging");
    markWidgetUsedIn("open_conversations", "messaging");
    markWidgetUsedIn("messages_attention", "messaging");
    markWidgetUsedIn("messaging_health", "messaging");

    localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify(Object.keys(initialWidgets)));
  }, [templateRefreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddWidget = (widgetId) => {
    if (widgets[widgetId]) {
      alert("Widget already exists on this page");
      return;
    }

    const component = resolveWidgetComponent(widgetId);
    if (!component) {
      alert("Unknown widget");
      return;
    }

    setWidgets((prev) => ({ ...prev, [widgetId]: component }));
    markWidgetUsedIn(widgetId, "messaging");

    setLayout((prev) => {
      const bottomY = prev.length === 0 ? 0 : Math.max(...prev.map((l) => l.y + l.h));
      const newLayout = [
        ...prev,
        { i: widgetId, x: 0, y: bottomY, w: 6, h: 4, minW: 4, minH: 3 },
      ];

      localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(newLayout));
      localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify([...Object.keys(widgets), widgetId]));
      return newLayout;
    });

    setShowAddModal(false);
  };

  const handleRemoveWidget = (widgetId) => {
    setWidgets((prev) => {
      const clone = { ...prev };
      delete clone[widgetId];
      return clone;
    });

    setLayout((prev) => {
      const filtered = prev.filter((item) => item.i !== widgetId);
      localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(filtered));
      localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify(Object.keys(widgets).filter((id) => id !== widgetId)));
      return filtered;
    });
  };

  const onLayoutChange = useCallback(
    (newLayout) => {
      const normalised = normaliseLayout(newLayout);
      setLayout(normalised);
      localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(normalised));
      localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify(Object.keys(widgets)));
    },
    [widgets]
  );

  const getWidgetTitle = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  /*
   * Mobile is a presentation of the existing Messaging configuration, not a
   * second editable layout. Follow the saved desktop visual order without
   * persisting mobile positions back into messagingLayout.
   */
  const mobileWidgetEntries = Object.entries(widgets).sort(([a], [b]) => {
    const aIndex = layout.findIndex((item) => item.i === a);
    const bIndex = layout.findIndex((item) => item.i === b);

    const aLayout = aIndex >= 0 ? layout[aIndex] : null;
    const bLayout = bIndex >= 0 ? layout[bIndex] : null;

    const ay = Number.isFinite(aLayout?.y) ? aLayout.y : Number.MAX_SAFE_INTEGER;
    const by = Number.isFinite(bLayout?.y) ? bLayout.y : Number.MAX_SAFE_INTEGER;
    if (ay !== by) return ay - by;

    const ax = Number.isFinite(aLayout?.x) ? aLayout.x : Number.MAX_SAFE_INTEGER;
    const bx = Number.isFinite(bLayout?.x) ? bLayout.x : Number.MAX_SAFE_INTEGER;
    if (ax !== bx) return ax - bx;

    return aIndex - bIndex;
  });

  const getMobileWidgetHeight = (widgetId) => {
    const item = layout.find((layoutItem) => layoutItem.i === widgetId);
    const h = Number.isFinite(item?.h) ? Math.max(1, item.h) : 4;

    return Math.max(
      280,
      h * rowHeight + Math.max(0, h - 1) * 24
    );
  };

  return (
    <div
      className={isMobile ? undefined : "p-6"}
      style={{
        backgroundColor: "#f0f0f0",
        minHeight: isMobile ? "100%" : "100vh",
        overflowX: "hidden",
        paddingTop: isMobile ? "4px" : undefined,
        paddingBottom: isMobile ? "16px" : undefined,
        paddingLeft: isMobile ? "12px" : containerPadding,
        paddingRight: isMobile ? "12px" : containerPadding,
        boxSizing: "border-box",
      }}
    >
      <div ref={gridRef} className="w-full" style={{ minWidth: 0 }}>
        <div
          className={isMobile ? undefined : "flex justify-between items-center mb-1"}
          style={{
            minHeight: isMobile ? "58px" : headerHeight,
            height: isMobile ? "auto" : headerHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: isMobile ? "12px" : undefined,
            marginBottom: isMobile ? "6px" : undefined,
          }}
        >
          <h2
            className={isMobile ? undefined : "text-2xl font-semibold"}
            style={{
              fontSize: isMobile ? "1.35rem" : undefined,
              fontWeight: 600,
              lineHeight: 1.2,
              margin: 0,
              minWidth: 0,
            }}
          >
            Messaging Centre
          </h2>
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white"
            style={{
              backgroundColor: theme.button_action?.normal || "#000",
              color: "#fff",
              padding: isMobile ? "10px 14px" : "10px 18px",
              minHeight: isMobile ? "44px" : undefined,
              whiteSpace: "nowrap",
              borderRadius: "6px",
              fontWeight: "bold",
            }}
          >
            Add Widget
          </button>
        </div>

        {isMobile ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              width: "100%",
              minWidth: 0,
              paddingBottom: "8px",
            }}
          >
            {mobileWidgetEntries.map(([key, WidgetComp]) => (
              <div
                key={key}
                style={{
                  width: "100%",
                  minWidth: 0,
                  height: `${getMobileWidgetHeight(key)}px`,
                  overflow: "hidden",
                }}
              >
                <WidgetWrapper title={getWidgetTitle(key)} onRemove={() => handleRemoveWidget(key)}>
                  {React.isValidElement(WidgetComp) ? WidgetComp : <div className="text-red-500">Invalid Widget</div>}
                </WidgetWrapper>
              </div>
            ))}
          </div>
        ) : (
          <GridLayout
            className="layout"
            layout={layout}
            cols={12}
            rowHeight={rowHeight}
            width={gridWidth}
            draggableHandle=".widget-drag-handle"
            resizeHandles={["se"]}
            onLayoutChange={onLayoutChange}
            compactType={null}
            preventCollision={false}
            margin={[16, 24]}
          >
            {Object.entries(widgets).map(([key, WidgetComp]) => (
              <div key={key}>
                <WidgetWrapper title={getWidgetTitle(key)} onRemove={() => handleRemoveWidget(key)}>
                  {React.isValidElement(WidgetComp) ? WidgetComp : <div className="text-red-500">Invalid Widget</div>}
                </WidgetWrapper>
              </div>
            ))}
          </GridLayout>
        )}

        {showAddModal && (
          <AddWidgetModal onClose={() => setShowAddModal(false)} onSelect={handleAddWidget} />
        )}

        {/* ✅ Page-level portal modals live here (no widget clipping) */}
        <WhatsAppTemplateCreatorModal
          isOpen={showWaCreator}
          tenantId={waCreatorTenantId}
          onClose={() => setShowWaCreator(false)}
          onCreated={() => setTemplateRefreshKey((k) => k + 1)}
        />
      </div>
    </div>
  );
};

export default MessagingPage;
